<!--@format-->
<template>
    <div class="start-call-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="widget-title">
                    <g3-call-svg />
                    <h3>Start a Call</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading" class="step">
                    <loading-screen-svg />
                </div>
                <div v-else class="call-container">
                    <div class="default-number-container">
                        <div class="title">Make A Call From</div>
                        // TODO KYM: still need to add dropdown with default selected
                        <div>
                            {{
                                `${defaultPhoneNumber.number} ${defaultPhoneNumber.name} (Default)`
                            }}
                        </div>
                    </div>
                    <div v-show="isAddingNewContact" class="outlined-section">
                        <div class="section-title">Add Phone Number</div>
                        <div class="name-container">
                            <div class="title">Contact Name <span class="required">*</span></div>
                            <v-autocomplete
                                v-if="!widget.contact.id"
                                v-model="selectedContact"
                                class="contact-dropdown app-select"
                                :items="contacts"
                                outlined
                                rounded
                                :menu-props="{
                                    contentClass: 'contact-dropdown-content',
                                    closeOnContentClick: true,
                                }"
                                :placeholder="'Choose a contact'"
                            >
                                <template #item="{ item }">
                                    <div class="contact-name-item">
                                        <app-profile-image :src="item.profileImageUrl" />
                                        <span>{{ `${item.firstName} ${item.lastName}` }}</span>
                                    </div>
                                </template>
                                <template #selection="{ item }">
                                    <div class="contact-name-selection">
                                        <app-profile-image :src="item.profileImageUrl" />
                                        <span>{{ `${item.firstName} ${item.lastName}` }}</span>
                                    </div>
                                </template>
                            </v-autocomplete>
                        </div>
                        <div class="type-container">
                            <div class="title">Phone Type <span class="required">*</span></div>
                            <v-autocomplete
                                v-model="availablePhoneType"
                                class="phone-type-dropdown app-select"
                                outlined
                                rounded
                                item-text="name"
                                item-value="name"
                                :items="phoneTypeOptions"
                                :menu-props="{
                                    contentClass: 'phone-type-dropdown-content',
                                    closeOnContentClick: true,
                                }"
                                :placeholder="'Choose a phone type'"
                            >
                                <template #item="{ item }">
                                    <div
                                        class="phone-type-item"
                                        :class="{ 'disabled-type': item.disabled }"
                                    >
                                        <div class="type-name">{{ item.name }}</div>
                                        <div v-if="item.disabled" class="already-in-use">
                                            Already in use
                                        </div>
                                    </div>
                                </template>
                                <template #selection="{ item }">
                                    <div class="phone-type-selection">
                                        <span>{{ item.name }}</span>
                                    </div>
                                </template>
                            </v-autocomplete>
                        </div>
                        <div class="phone-container">
                            <label>Phone Number</label>
                            <phone-country-selector
                                id="phoneNumber1"
                                v-model="formattedNumber"
                                :error="hasInteractedWithPhoneInput && !isValid"
                                clearable
                                default-country-code="US"
                                @focus="hasInteractedWithPhoneInput = true"
                                @update="onUpdate"
                            />
                            <div class="button-container">
                                <app-button class="cancel-button" @click="cancel"
                                    >Cancel</app-button
                                >
                                <app-button
                                    class="add-phone-number-button"
                                    :disabled="!isValid"
                                    @click="savePhoneNumber"
                                    >Save Phone Number</app-button
                                >
                            </div>
                        </div>
                    </div>
                    <div v-show="!isAddingNewContact" class="contact-dropdown-wrapper">
                        <div class="title">Contact <span v-if="!widget.contact.id" class="required">*</span></div>
                        <div v-if="!widget.contact.id">
                            <contact-phone-number-dropdown
                                v-model="selectedContact"
                                :items="items"
                                @add-contact="addContact"
                            />
                            <div class="helper-text">
                                Select a contact or enter a number you would like to call.
                            </div>
                        </div>
                        <div v-else class="call-back-contact-wrapper">
                            <app-profile-image :src="widget.contact.profileImageUrl" />
                            <div class="contact-information-container">
                                <div class="contact-information">{{ widget.contact.name }} from {{ widget.contact.company.name }}</div>
                                <div class="phone-number-information">{{ widget.contact.countryCode }} {{ widget.contact.phone }}   {{ widget.contact.phoneType }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Close
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isCallDisabled" class="call-btn" @click="call"
                        >Call</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const ContactPhoneNumberDropdown = () =>
    import('@/components/contact/contact-phone-number-dropdown');
const G3CallSvg = () => import('@/components/svg/grid/g3-call-svg');
const PhoneCountrySelector = () =>
    import('@/components/phone-country-input/phone-country-selector');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'start-call-widget',

    components: {
        'app-button': AppButton,
        'app-profile-image': AppProfileImage,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'contact-phone-number-dropdown': ContactPhoneNumberDropdown,
        'g3-call-svg': G3CallSvg,
        'phone-country-selector': PhoneCountrySelector,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            hasInteractedWithPhoneInput: false,
            formattedNumber: '',
            isAddingNewContact: false,
            selectedContact: null,

            defaultPhoneNumber: {
                name: 'Default Phone Number',
                number: '+1 123-456-7890',
            },

            phoneNumberTypes: [
                { id: 1, name: 'Work Phone' },
                { id: 2, name: 'Home Phone' },
                { id: 3, name: 'Mobile Phone' },
                { id: 4, name: 'Teams Phone' },
            ],

            items: [
                {
                    key: '1',
                    id: '1',
                    firstName: 'Jane',
                    lastName: 'Doe',
                    name: 'Jane Doe',
                    phoneNumber: '123-456-7890',
                    type: {
                        id: '1',
                        name: 'Work Phone',
                    },
                    profileImageUrl: require('@/assets/images/avatar.png'),
                    company: {
                        id: '6',
                        name: 'Doe Enterprises',
                    },
                },
                {
                    key: '2',
                    id: '1',
                    firstName: 'Jane',
                    lastName: 'Doe',
                    name: 'Jane Doe',
                    phoneNumber: '987-654-3210',
                    type: {
                        id: '2',
                        name: 'Mobile Phone',
                    },

                    profileImageUrl: require('@/assets/images/avatar.png'),
                    company: {
                        id: '7',
                        name: 'Doe Enterprises',
                    },
                },
                {
                    key: '3',
                    id: '2',
                    firstName: 'John',
                    lastName: 'Smith',
                    name: 'John Smith',
                    phoneNumber: '555-000-1234',
                    type: {
                        id: '3',
                        name: 'Teams Phone',
                    },
                    profileImageUrl: require('@/assets/images/avatar.png'),
                    company: {
                        id: '8',
                        name: 'Smith Co.',
                    },
                },
                {
                    key: '4',
                    id: '2',
                    firstName: 'John',
                    lastName: 'Smith',
                    name: 'John Smith',
                    phoneNumber: '555-111-2222',
                    type: {
                        id: '4',
                        name: 'Home Phone',
                    },
                    profileImageUrl: require('@/assets/images/avatar.png'),
                    company: {
                        id: '9',
                        name: 'Smith Co.',
                    },
                },
                {
                    key: '5',
                    id: '3',
                    firstName: 'Alice',
                    lastName: 'Johnson',
                    name: 'Alice Johnson',
                    phoneNumber: '444-333-2222',
                    type: {
                        id: '2',
                        name: 'Mobile Phone',
                    },
                    profileImageUrl: require('@/assets/images/avatar.png'),
                    company: {
                        id: '10',
                        name: 'Johnson & Associates',
                    },
                },
            ],
        };
    },

    computed: {
        isValid() {
            if (!this.isAddingNewContact) return !!this.selectedContact;
            return (
                this.selectedContact?.firstName &&
                this.selectedContact?.lastName &&
                this.formattedNumber &&
                this.availablePhoneType
            );
        },

        contacts() {
            return Array.from(new Map(this.items.map((item) => [item.id, item])).values());
        },

        usedPhoneTypes() {
            if (!this.selectedContact) return [];
            return this.items
                .filter((c) => c.id === this.selectedContact.id)
                .map((c) => c.type.name);
        },

        phoneTypeOptions() {
            return this.phoneNumberTypes.map((type) => ({
                ...type,
                disabled: this.usedPhoneTypes.includes(type.name),
            }));
        },

        availablePhoneType() {
            return this.phoneTypeOptions.find((type) => !type.disabled) || null;
        },

        isCallDisabled() {
            if (!this.isAddingNewContact) {
                return !this.selectedContact;
            }

            return true;
        },
    },

    created() {
        this.isLoading = false;
        this.selectedContact = null;
    },

    methods: {
        addContact() {
            this.isAddingNewContact = true;
            this.selectedContact = null;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        call() {
            EventBus.$emit('open-call-module', {
                options: {
                    clientId: this.widget.options.clientId,
                    userId: this.widget.options.userId,
                    contactId: this.widget.options.contactId,
                },
            });
            EventBus.$emit('close-widget', this.widget);
        },

        onUpdate({ formattedNumber, isValid }) {
            this.formattedNumber = formattedNumber;
            this.isValid = isValid;
        },

        cancel() {
            this.isAddingNewContact = false;
        },

        createNewContact() {
            return {
                key: String(Date.now()),
                id: this.selectedContact?.id || '',
                firstName: this.selectedContact?.firstName || '',
                lastName: this.selectedContact?.lastName || '',
                name: `${this.selectedContact?.firstName || ''} ${this.selectedContact?.lastName || ''}`.trim(),
                phoneNumber: this.formattedNumber,
                type: {
                    id:
                        this.phoneNumberTypes.find((t) => t.name === this.availablePhoneType?.name)
                            ?.id || '',
                    name: this.availablePhoneType?.name || '',
                },
                profileImageUrl:
                    this.selectedContact?.profileImageUrl || require('@/assets/images/avatar.png'),
                company: this.selectedContact?.company || {},
            };
        },

        savePhoneNumber() {
            const newContact = this.createNewContact();

            const exists = this.items.some(
                (item) =>
                    item.phoneNumber === newContact.phoneNumber &&
                    item.type.name === newContact.type.name &&
                    item.id === newContact.id,
            );
            const isNewContactValid =
                !exists &&
                !!(
                    newContact.firstName &&
                    newContact.lastName &&
                    newContact.phoneNumber &&
                    newContact.type.name
                );

            if (isNewContactValid) {
                this.items.push(newContact);
                this.selectedContact = newContact;
                this.isAddingNewContact = false;
            }
        },
    },
};
</script>

<style>
.start-call-widget .widget-title {
    display: flex;
    align-items: center;
}

.start-call-widget .widget-title .g3-call-svg {
    margin-right: 16px;
}

.start-call-widget .title {
    font-weight: 500;
    font-size: 16px;
    margin-bottom: 8px;
    color: var(--color-text-default);
}

.start-call-widget .default-number-container,
.start-call-widget .contact-dropdown-wrapper {
    margin-bottom: 24px;
}

.start-call-widget .required {
    font-family: Roboto;
    font-weight: 500;
    font-size: 16px;
    color: var(--color-text-warning);
    margin-left: 8px;
}

.start-call-widget .contact-dropdown-wrapper .contact-select-dropdown {
    width: 100%;
}

.start-call-widget .v-text-field__details {
    display: none;
}

.start-call-widget .widget-contents-container .content-wrapper {
    padding: 24px;
}

.start-call-widget .widget-footer .transparent-btn {
    border: 1px solid var(--color-border-accent-silvergray);
}

.start-call-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5; /* TODO KIM: I don't see a variable for this color */
}

.start-call-widget .widget-footer .action-container .call-btn {
    background: var(--color-background-button-primary);
    color: #fff;
}

.start-call-widget .contact-dropdown-wrapper .title {
    display: flex;
}

.start-call-widget .helper-text {
    font-weight: 400;
    font-size: 12px;
    color: var(--color-text-subtle);
}

.start-call-widget .outlined-section {
    position: relative;
    border: 1px solid var(--color-border-accent-silvergray);
    border-radius: 16px;
    top: 16px;
    padding: 24px 24px 24px;
}

.start-call-widget .outlined-section:not(:last-child) {
    margin-bottom: 40px;
}

.start-call-widget .outlined-section .section-title {
    font: normal normal bold 18px/24px Roboto;
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    margin: auto;
    width: fit-content;
    padding: 0px 25px;
    background-color: var(--color-background-secondary);
    font-weight: 600;
}

.start-call-widget .phone-container .phone-country-selector {
    margin-bottom: 8px;
}

.start-call-widget .phone-container .button-container {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
}

.start-call-widget .phone-container .button-container .cancel-button {
    color: var(--color-text-button-secondary);
    border: 1px solid var(--color-border-button-secondary);
}

.start-call-widget .phone-container .button-container .add-phone-number-button {
    background: var(--color-background-button-primary);
    color: #fff;
}

.start-call-widget .transparent-btn {
    background: transparent;
    color: var(--color-text-subtle);
    border: none;
}

.start-call-widget .transparent-btn:hover {
    background: #e1e0e5; /* TODO KYM: I don't see a variable for this color */
}

/* Add Contact Dropdown */
.start-call-widget .contact-name-selection,
.contact-dropdown-content .contact-name-item {
    display: flex;
    align-items: center;
    font-size: 16px;
}

.contact-dropdown-content .v-list-item--link:before {
    background-color: #fff; /* TODO KYM: I don't see a correctly named variable for this color */
}

.start-call-widget .contact-name-selection .app-profile-image,
.contact-dropdown-content .contact-name-item .app-profile-image {
    margin-right: 8px;
}

.start-call-widget .contact-name-selection .app-profile-image .background-image,
.contact-dropdown-content .contact-name-item .app-profile-image .background-image {
    width: 32px;
    height: 32px;
}

.contact-dropdown-content .v-list-item {
    padding: 0 8px;
}

.start-call-widget
    .contact-dropdown.v-select.v-text-field--outlined:not(.v-text-field--single-line)
    .v-select__selections {
    padding: 0;
}

.start-call-widget .contact-dropdown.app-select.v-input > .v-input__control > .v-input__slot {
    padding: 0 10px 0 6px;
}

/* Phone Type Dropdown */
.start-call-widget .phone-type-dropdown .phone-type-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.start-call-widget .phone-type-dropdown .phone-type-item.disabled-type {
    color: #aaa; /* TODO KYM: No variable for this color? */
    background-color: #f9f9f9; /* TODO KYM: No variable for this color? */
    pointer-events: none;
}

.start-call-widget
    .phone-type-dropdown.v-select.v-text-field--outlined:not(.v-text-field--single-line)
    .v-select__selections {
    padding: 0;
}

/* Phone Type Dropdown */
.phone-type-dropdown-content.v-menu__content.v-menu__content--fixed.menuable__content__active.v-autocomplete__content {
    border-radius: 16px !important;
    border: 1px solid var(--color-border-accent-silvergray) !important;
}

.phone-type-dropdown-content .v-list-item {
    background-color: #ffffff; /* TODO KYM: I don't see a correctly named variable for this color */
    border-bottom: 1px solid var(--color-border-accent-silvergray) !important;
}

.phone-type-dropdown-content .v-list-item.v-list-item--disabled {
    background-color: #e9e9ef; /* TODO KYM: I don't see a correctly named variable for this color */
}

.phone-type-dropdown-content .v-list-item .phone-type-item.disabled-type .type-name {
    font-weight: 400;
    font-size: 12px;
    line-height: 12px;
    color: var(--text-color) !important;
    margin-bottom: 2px;
}

.phone-type-dropdown-content .v-list-item .phone-type-item.disabled-type .already-in-use {
    font-weight: 400;
    font-size: 10px;
    line-height: 10px;
    color: var(--color-text-subtle) !important;
}

.start-call-widget .app-select.v-input > .v-input__control > .v-input__slot {
    min-height: 40px;
    margin-bottom: 24px;
}

.start-call-widget .contact-phone-number-dropdown {
    margin-bottom: 8px;
}

.start-call-widget
    .contact-phone-number-dropdown
    .app-select.v-input
    > .v-input__control
    > .v-input__slot {
    margin-bottom: 0px;
}

.start-call-widget .content-wrapper .call-back-contact-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
}

.start-call-widget .content-wrapper .contact-information-container .contact-information {
    font-weight: 400;
    font-size: 16px;
    line-height: 25px;
}

.start-call-widget .content-wrapper .contact-information-container .phone-number-information {
    font-weight: 400;
    font-size: 12px;
    line-height: 12px;
    color: var(--color-text-subtle);
}
</style>
